/**
 * AchievementOverlay — Xbox-style bottom-left achievement toast notification.
 *
 * Implements the exact user timing specification for the first achievement of
 * a batch:
 * - t = 0.0s: Achievement unlocked, audio fanfare starts playing, screen remains blank.
 * - t = 7.1s: Toast slides up at bottom-left on the fanfare drop (Call of Duty / Xbox aesthetic).
 * - t = 10.5s: Toast begins subtle neon flicker (achievementNeonFlicker).
 * - t = 11.0s: Toast flares up with an intense neon flash / glint (achievementNeonFlash).
 * - t = 11.6s: Toast smoothly fades out into transparency (achievementFadeOut).
 * - t = 12.2s: Toast is hidden; next queued achievement begins its cycle.
 *
 * When several achievements unlock within the same batch (queue still
 * non-empty when the previous one finishes), only the first plays the
 * fanfare and waits out the 7.1s blank lead-in — every following one in
 * that run skips straight to the reveal, keeping the same hold/flicker/
 * flash/fadeout timing relative to its own reveal.
 *
 * Aesthetic: Cool Dark with Hue Neon Glow, focused on crisp, puristic typography.
 * Always renders at z-index: 15000 above all modals and gameplay canvases.
 */

import { buildAchievementIconBitmap } from '../engine/render/achievementIcons.js';

const T_REVEAL_MS = 7100;
const T_FLICKER_MS = 10500;
const T_FLASH_MS = 11000;
const T_FADEOUT_MS = 11600;
const T_COMPLETE_MS = 12200;

// Hold/flicker/flash/fadeout timing stays identical for every achievement;
// only the lead-in before the reveal differs between first and follow-up.
const D_FLICKER_MS = T_FLICKER_MS - T_REVEAL_MS;
const D_FLASH_MS = T_FLASH_MS - T_REVEAL_MS;
const D_FADEOUT_MS = T_FADEOUT_MS - T_REVEAL_MS;
const D_COMPLETE_MS = T_COMPLETE_MS - T_REVEAL_MS;

export class AchievementOverlay {
  constructor(audioOrOpts = null, container = null) {
    if (audioOrOpts && typeof audioOrOpts === 'object' && ('audio' in audioOrOpts || 'container' in audioOrOpts)) {
      this.audio = audioOrOpts.audio || null;
      this.container = container || audioOrOpts.container || (typeof document !== 'undefined' ? document.body : null);
    } else {
      this.audio = audioOrOpts;
      this.container = container || (typeof document !== 'undefined' ? document.body : null);
    }
    this.el = null;
    this._queue = [];
    this._activeTimers = [];
    this._isPlaying = false;
    this._isFirstInBatch = true;
    this._lastPlayedPopupType = null;
    if (this.container) {
      this._initElements();
    }
  }

  _initElements() {
    let el = document.getElementById('achievement-toast-overlay');
    if (!el) {
      el = document.createElement('div');
      el.id = 'achievement-toast-overlay';
      el.className = 'achievement-toast-overlay hidden';
      this.container.appendChild(el);
    }
    this.el = el;
  }

  /**
   * Enqueues an achievement definition or quest/jackpot item to be displayed.
   * @param {Object} def - Achievement definition from ACHIEVEMENTS catalog, or quest/jackpot item
   */
  show(def) {
    if (!def) return;
    const item = { ...def };
    if (!item.kind) {
      if (item.isJackpot || item.type === 'jackpot' || item.id === 'daily_jackpot' || item.name === 'DAILY JACKPOT' || item.title === 'DAILY JACKPOT') {
        item.kind = 'jackpot';
      } else if (item.rewardFugazi !== undefined || (item.id && String(item.id).startsWith('directive'))) {
        item.kind = 'quest';
      } else {
        item.kind = 'achievement';
      }
    }
    if (!item.id) {
      item.id = `${item.kind}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    }
    this._queue.push(item);
    if (!this._isPlaying) {
      this._isFirstInBatch = true;
      this._playNext();
    }
  }

  /**
   * Enqueues a completed daily quest (directive) to be displayed in quest green.
   * @param {Object} directive - Directive object from directives manager
   */
  showDirective(directive) {
    if (!directive) return;
    const item = {
      id: directive.id || `directive_${Date.now()}`,
      kind: 'quest',
      tier: directive.tier || 1,
      name: directive.title || directive.name || 'Daily Quest',
      description: directive.description || '',
      rewardFugazi: directive.rewardFugazi || 0,
      rewardXP: directive.rewardXP || 0,
      rewardRankBonus: directive.rewardRankBonus || 0,
      icon: directive.icon || 'dailyProtocol'
    };
    this.show(item);
  }

  /** Alias for showDirective */
  showQuest(directive) {
    this.showDirective(directive);
  }

  /**
   * Enqueues the Daily Jackpot unlock to be displayed in jackpot gold.
   * @param {Object} [details] - Optional jackpot reward details
   */
  showJackpot(details = {}) {
    const item = {
      id: `jackpot_${Date.now()}`,
      kind: 'jackpot',
      tier: 'gold',
      name: details.title || details.name || 'DAILY JACKPOT',
      description: details.description || "All 3 daily quests completed!",
      rewardFugazi: details.fugazi || details.rewardFugazi || 100,
      rewardXP: details.xp || details.rewardXP || 50,
      icon: details.icon || '🏆'
    };
    this.show(item);
  }

  _clearTimers() {
    for (const timer of this._activeTimers) {
      clearTimeout(timer);
    }
    this._activeTimers = [];
  }

  _playNext() {
    if (this._queue.length === 0) {
      this._isPlaying = false;
      this._isFirstInBatch = true;
      return;
    }

    this._isPlaying = true;
    this._clearTimers();

    const def = this._queue[0];
    const isFirst = this._isFirstInBatch;
    this._isFirstInBatch = false;

    // 1. Play fanfare sound and determine lead-in timing:
    // Only achievement.mp3 has a 7.1s build-up; short sound effects (daily quest, daily jackpot,
    // or custom assigned SFX) reveal immediately so the audio and visual toast land together.
    const popupType = (def.kind === 'jackpot')
      ? 'daily_jackpot'
      : (def.kind === 'directive' || def.kind === 'quest')
        ? 'daily_quest'
        : 'achievement';

    const shouldPlaySound = isFirst || (this._lastPlayedPopupType !== popupType);
    this._lastPlayedPopupType = popupType;

    let leadInMs = 0;
    if (shouldPlaySound) {
      try {
        if (typeof this.audio?.playPopupFanfare === 'function') {
          const res = this.audio.playPopupFanfare(popupType);
          leadInMs = (res && typeof res.leadInMs === 'number') ? res.leadInMs : 0;
        } else if (typeof this.audio?.playAchievementFanfare === 'function') {
          const res = this.audio.playAchievementFanfare(popupType);
          leadInMs = (res && typeof res.leadInMs === 'number') ? res.leadInMs : (popupType === 'achievement' ? T_REVEAL_MS : 0);
        }
      } catch (err) {
        console.warn('[AchievementOverlay] Audio fanfare failed:', err);
      }
    }

    // Ensure element exists
    if (!this.el) this._initElements();
    if (!this.el) {
      this._queue.shift();
      this._isPlaying = false;
      return;
    }

    // Prepare markup while still hidden
    const kind = def.kind || 'achievement';
    const isQuest = kind === 'quest' || kind === 'directive';
    const isJackpot = kind === 'jackpot';

    let boxClasses = 'achievement-toast-box';
    let eyebrowText = 'ACHIEVEMENT UNLOCKED';
    let titleText = def.name || def.title || '';
    let descText = def.description || '';
    let fallbackEmoji = '🏆';
    let iconKey = def.icon;

    if (isQuest) {
      const questTier = def.tier || 1;
      boxClasses += ` toast-quest tier-${questTier}`;
      eyebrowText = 'DAILY QUEST COMPLETED';
      fallbackEmoji = '✓';
      if (!iconKey || iconKey === '✓' || iconKey === '🎯') {
        iconKey = 'dailyProtocol';
      }
    } else if (isJackpot) {
      boxClasses += ` toast-jackpot tier-gold`;
      eyebrowText = 'DAILY JACKPOT';
      fallbackEmoji = '🏆';
      if (!iconKey) iconKey = '🏆';
    } else {
      const tier = def.tier || 'bronze';
      boxClasses += ` tier-${tier}`;
      eyebrowText = 'ACHIEVEMENT UNLOCKED';
      fallbackEmoji = '🏆';
    }

    let iconUrl = '';
    try {
      if (iconKey && iconKey !== '🏆' && iconKey !== '✓') {
        iconUrl = buildAchievementIconBitmap(iconKey, 80).toDataURL();
      }
    } catch (err) {
      console.warn('[AchievementOverlay] Failed to render icon bitmap:', err);
    }

    let badgesHtml = '';
    if (isQuest) {
      if (def.rewardFugazi) {
        badgesHtml += `<span class="achievement-toast-xp-badge quest-badge">+${def.rewardFugazi} FUGAZI</span>`;
      }
      if (def.rewardXP) {
        badgesHtml += `<span class="achievement-toast-xp-badge quest-badge-xp">+${def.rewardXP} XP</span>`;
      }
      if (!badgesHtml) {
        badgesHtml = `<span class="achievement-toast-xp-badge quest-badge">COMPLETED</span>`;
      }
    } else if (isJackpot) {
      const fugaziVal = def.rewardFugazi !== undefined ? def.rewardFugazi : 100;
      const xpVal = def.rewardXP !== undefined ? def.rewardXP : 50;
      badgesHtml = `
        <span class="achievement-toast-xp-badge jackpot-badge">+${fugaziVal} FUGAZI</span>
        <span class="achievement-toast-xp-badge jackpot-badge-xp">+${xpVal} XP</span>
      `;
    } else {
      const xpText = def.xp > 0 ? `+${def.xp} XP` : 'MAX';
      badgesHtml = `<span class="achievement-toast-xp-badge">${xpText}</span>`;
    }

    const tierAttr = def.tier || (isJackpot ? 'gold' : (isQuest ? 'quest' : 'bronze'));

    this.el.innerHTML = `
      <div class="${boxClasses}" data-tier="${tierAttr}" data-kind="${kind}">
        <div class="achievement-toast-icon-wrap">
          ${iconUrl ? `<img src="${iconUrl}" alt="${titleText}" class="achievement-toast-icon-img">` : `<span class="achievement-toast-icon-fallback">${fallbackEmoji}</span>`}
        </div>
        <div class="achievement-toast-content">
          <div class="achievement-toast-eyebrow">
            <span class="achievement-toast-eyebrow-pip"></span>
            <span class="achievement-toast-eyebrow-text">${eyebrowText}</span>
          </div>
          <div class="achievement-toast-title" title="${titleText}">${titleText}</div>
          <div class="achievement-toast-meta">
            <span class="achievement-toast-desc">${descText}</span>
            ${badgesHtml}
          </div>
        </div>
        <div class="achievement-toast-glow-edge"></div>
      </div>
    `;

    // Reset all animation states
    this.el.className = 'achievement-toast-overlay hidden';

    // 2. Reveal toast (Xbox slide-up).
    // Sounds with build-up (e.g. achievement.mp3) wait out their lead-in (7.1s);
    // short sound effects reveal immediately so audio and visual toast land together.
    const revealDelay = isFirst ? leadInMs : 0;
    const tReveal = setTimeout(() => {
      if (!this.el) return;
      this.el.classList.remove('hidden');
      this.el.classList.remove('achievement-toast-flicker', 'achievement-toast-flash', 'achievement-toast-out');
      void this.el.offsetWidth; // force reflow
      this.el.classList.add('achievement-toast-in');
    }, revealDelay);
    this._activeTimers.push(tReveal);

    // 3. Neon flicker starts (same hold time after reveal for every achievement)
    const tFlicker = setTimeout(() => {
      if (!this.el) return;
      this.el.classList.add('achievement-toast-flicker');
    }, revealDelay + D_FLICKER_MS);
    this._activeTimers.push(tFlicker);

    // 4. Intense neon flash / flare
    const tFlash = setTimeout(() => {
      if (!this.el) return;
      this.el.classList.add('achievement-toast-flash');
    }, revealDelay + D_FLASH_MS);
    this._activeTimers.push(tFlash);

    // 5. Smooth fadeout
    const tFadeout = setTimeout(() => {
      if (!this.el) return;
      this.el.classList.remove('achievement-toast-in');
      this.el.classList.add('achievement-toast-out');
    }, revealDelay + D_FADEOUT_MS);
    this._activeTimers.push(tFadeout);

    // 6. Complete cycle and advance queue
    const tComplete = setTimeout(() => {
      if (!this.el) return;
      this.el.classList.add('hidden');
      this.el.classList.remove('achievement-toast-flicker', 'achievement-toast-flash', 'achievement-toast-out');
      this._queue.shift();
      this._playNext();
    }, revealDelay + D_COMPLETE_MS);
    this._activeTimers.push(tComplete);
  }

  /**
   * Immediately clears any active playback and resets queue.
   */
  reset() {
    this._clearTimers();
    this._queue = [];
    this._isPlaying = false;
    this._isFirstInBatch = true;
    this._lastPlayedPopupType = null;
    if (this.el) {
      this.el.className = 'achievement-toast-overlay hidden';
      this.el.innerHTML = '';
    }
  }
}

export { AchievementOverlay as QuestOverlay, AchievementOverlay as DirectiveOverlay };
